import { Checkbox, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { TraineeEvaluationDto } from "../../../types/applicationTypes";
import { Cancel, CheckCircle } from "@mui/icons-material";
import { doesPermissionsHaveSomeOf } from "../../../utils/authUtils";
import { AuthContext } from "../../../context/auth-context/AuthContext";

/**
 * The SelectionTableProps interface defines the props for the SelectionTable component
 */
interface SelectionTableProps {
  applicantsEvaluations: TraineeEvaluationDto[];
  selectedApplicants: TraineeEvaluationDto[];
  setSelectedApplicants: React.Dispatch<React.SetStateAction<TraineeEvaluationDto[]>>;
  tableType: "invite" | "admit";
}
/**
 * The SelectionTable component displays a table with all applicants and the possibility to select them for the selection weekend
 * @param applicantsEvaluations The evaluations of the applicants
 * @param selectedApplicants The selected applicants
 * @param setSelectedApplicants The function to set the selected applicants
 * @param tableType The type of the table
 * @returns The SelectionTable component
 */
const SelectionTable = ({
  applicantsEvaluations,
  selectedApplicants,
  setSelectedApplicants,
  tableType,
}: SelectionTableProps) => {
  const styles = {
    tableHeader: {
      backgroundColor: "primary.main",
      color: "primary.contrastText",
      textAlign: "center",
      whiteSpace: "nowrap",
      overflow: "hidden",
    },
    tableHeaderCell: {
      color: "white",
      fontWeight: "bold",
    },
    tableRow: {
      "&:nth-of-type(odd)": { backgroundColor: "#fff" },
      "&:nth-of-type(even)": { backgroundColor: "#ededed" },
      textDecoration: "none",
    },
  };

  // Sort the applicants by the sum of their evaluations
  const applicants = applicantsEvaluations.sort(
    (a, b) =>
      b.evaluations.reduce((acc, curr) => acc + curr.evaluation, 0) -
      a.evaluations.reduce((acc, curr) => acc + curr.evaluation, 0)
  );

  /**
   * The handleSelectApplicant function selects or deselects an applicant
   * @param traineeApplicantId The id of the trainee applicant
   */
  const handleSelectApplicant = (traineeApplicantId: number) => {
    const selected = selectedApplicants.some((selected) => selected.traineeApplicantId === traineeApplicantId);
    if (selected) {
      const newSelectedApplicants = selectedApplicants.filter(
        (selected) => selected.traineeApplicantId !== traineeApplicantId
      );
      setSelectedApplicants(newSelectedApplicants);
    } else {
      const newSelectedApplicants = [
        ...selectedApplicants,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        applicantsEvaluations.find((applicant) => applicant.traineeApplicantId === traineeApplicantId)!,
      ];
      setSelectedApplicants(newSelectedApplicants);
    }
  };

  /**
   * The functionAvailabilityText function returns the text for the availability
   * @param availability The availability of the applicant for the selection weekend
   * @returns The text for the availability
   */
  const functionAvailabilityText = (availability: "nichtFR" | "nichtSA" | "nichtSO" | "kannImmer" | null) => {
    switch (availability) {
      case "kannImmer":
        return "Kann immer";
      case "nichtFR":
        return "Nicht Freitag";
      case "nichtSA":
        return "Nicht Samstag";
      case "nichtSO":
        return "Nicht Sonntag";
      default:
        return "Nicht definiert";
    }
  };

  const { auth } = useContext(AuthContext);
  const hasAdminPermission = doesPermissionsHaveSomeOf(auth.permissions, [16]);

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead sx={styles.tableHeader}>
          <TableRow>
            <TableCell sx={styles.tableHeaderCell}>{tableType === "invite" ? "Einladen?" : "Aufnehmen?"}</TableCell>
            <TableCell sx={styles.tableHeaderCell}>Bewerber</TableCell>
            <TableCell sx={styles.tableHeaderCell}>Gesamtbewertung</TableCell>
            <TableCell sx={styles.tableHeaderCell}>Anzahl Bewertungen</TableCell>
            <TableCell sx={styles.tableHeaderCell}>Auswahlwochenende</TableCell>
            <TableCell sx={styles.tableHeaderCell}>WW</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {applicants.map((evaluation, index) => (
            <TableRow key={`${evaluation.firstName}-${evaluation.lastName}-${index}`} sx={styles.tableRow}>
              {tableType === "invite" && evaluation.invited ? (
                <TableCell>eingeladen</TableCell>
              ) : tableType === "admit" && evaluation.toBeAdmitted ? (
                <TableCell>aufgenommen</TableCell>
              ) : (
                <TableCell>
                  {hasAdminPermission && (
                    <Checkbox
                      checked={selectedApplicants.some(
                        (selected) => selected.traineeApplicantId === evaluation.traineeApplicantId
                      )}
                      size="small"
                      onChange={() => handleSelectApplicant(evaluation.traineeApplicantId)}
                    />
                  )}
                </TableCell>
              )}
              <TableCell>
                <Link
                  to={`/traineebewerbungen/${evaluation.traineeApplicantId}`}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  {evaluation.lastName + ", " + evaluation.firstName}
                </Link>
              </TableCell>
              <TableCell>{evaluation.evaluations.reduce((acc, curr) => acc + curr.evaluation, 0)}</TableCell>
              <TableCell>{evaluation.evaluations.length}</TableCell>
              <TableCell>{functionAvailabilityText(evaluation.availabilitySelectionWeekend)}</TableCell>
              <TableCell>
                {evaluation.workingWeekend ? <CheckCircle color="success" /> : <Cancel color="error" />}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SelectionTable;
